import { FC, useState, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { getIsNewSeller } from '../../utils/sellerHelpers';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';

// components
import { Link } from 'dibs-elements/exports/Link';
import { FormattedMessage } from 'dibs-react-intl';
import { learnMore } from '../messages/pdpBioMessages';
import { SharedStarRating } from '../../shared/SharedStarRating/SharedStarRating';
import { PdpSharedNewSellerPill } from '../PdpSharedNewSellerPill/PdpSharedNewSellerPill';
import { PdpSharedDealerDetailsFeedback } from '../PdpSharedDealerDetailsFeedback/PdpSharedDealerDetailsFeedback';
import { PdpSharedDealerDetailsBioModalLazy } from '../PdpSharedDealerDetailsBio/PdpSharedDealerDetailsBioModalLazy';

// types
import {
    PdpSharedDealerDetailsHeader_seller$key as Seller,
    PdpSharedDealerDetailsHeader_seller$data,
} from './__generated__/PdpSharedDealerDetailsHeader_seller.graphql';
import { PdpSharedDealerDetailsHeader_user$key as User } from './__generated__/PdpSharedDealerDetailsHeader_user.graphql';

// styles
import styles from './PdpSharedDealerDetailsHeader.scss';

const shouldShowBioModal = (seller: PdpSharedDealerDetailsHeader_seller$data): boolean => {
    let hasVideo = false;
    const isRewardsTier2 = seller?.isRewardsTier2;
    const mediaList =
        seller?.liveStorefrontProfile?.galleryAlbum?.mediaList?.filter(media => {
            const element = media?.element;
            const { url, path } = element || {};
            if (url) {
                hasVideo = true;
            }
            return path || url;
        }) || [];

    return !isRewardsTier2 && (hasVideo || mediaList.length > 1);
};

const userFragment = graphql`
    fragment PdpSharedDealerDetailsHeader_user on User {
        canShowSellerName
    }
`;

const sellerFragment = graphql`
    fragment PdpSharedDealerDetailsHeader_seller on Seller {
        ...SharedStarRating_seller
        ...PdpSharedDealerDetailsFeedback_seller
        ...PdpSharedDealerDetailsBioModalLazy_seller
        isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
        monthsSinceStartDate: timeSinceStartDate(unit: MONTH)
        sellerProfile {
            company
        }
        shippingAddress {
            displayFullCityStateCountry: displayCityStateCountry(fullCountryName: true)
        }
        liveStorefrontProfile {
            headshots: photos(photoType: DEALER_STOREFRONT_HEADSHOT) {
                status
                path
            }
            galleryAlbum {
                mediaList {
                    element {
                        ... on Photo {
                            path
                        }
                        ... on Video {
                            url
                        }
                    }
                }
            }
        }
    }
`;

type Props = {
    user: User;
    seller: Seller;
    isClient: boolean;
    isMobile?: boolean;
    inlineHeader?: boolean;
    inlineBioLink?: boolean;
    isMobilePdpRedesign?: boolean;
};

export const PdpSharedDealerDetailsHeader: FC<Props> = ({
    isMobile,
    isClient,
    user: userRef,
    seller: sellerRef,
    inlineHeader = true,
    inlineBioLink = false,
    isMobilePdpRedesign,
}) => {
    const user = useFragment(userFragment, userRef);
    const seller = useFragment(sellerFragment, sellerRef);
    const { isRewardsTier2, shippingAddress, monthsSinceStartDate, liveStorefrontProfile } =
        seller || {};
    const canShowSellerName = user?.canShowSellerName;
    const isNewSeller = getIsNewSeller(monthsSinceStartDate);
    const dealerLocation = shippingAddress?.displayFullCityStateCountry;
    const { headshots, galleryAlbum } = liveStorefrontProfile || {};
    const { status: headshotStatus, path: headshotPath } = headshots?.[0] || {};
    const showHeadshot = isRewardsTier2 && headshotStatus === 'ACTIVE' && !!headshotPath;
    const mediaList = galleryAlbum?.mediaList || [];

    const [isBioModalOpen, setIsBioModalOpen] = useState(false);

    let sellerImagePath;

    if (showHeadshot) {
        sellerImagePath = headshotPath;
    } else if (mediaList.length) {
        sellerImagePath = mediaList.find(media => media?.element?.path)?.element?.path;
    }

    const detailsHeader = (
        <span data-tn="pdp-shared-dealer-details-header" className={styles.headerPrimary}>
            {canShowSellerName ? (
                <span>{seller?.sellerProfile?.company}</span>
            ) : (
                <FormattedMessage
                    id="PdpSharedDealerDetailsHeader.seller_title"
                    defaultMessage="About the Seller"
                />
            )}
            {isNewSeller && (
                <span className={styles.pill}>
                    <PdpSharedNewSellerPill />
                </span>
            )}
        </span>
    );
    const sellerImage = sellerImagePath ? (
        <img
            alt="" // decorative image
            sizes="(max-width:768px) 60px, 80px, 100px"
            src={sellerImagePath}
            className={styles.image}
            srcSet={getSrcsetString(sellerImagePath, [100, 200, 300])}
        />
    ) : null;

    const openBioModal = useCallback(() => setIsBioModalOpen(true), []);
    const closeBioModal = useCallback(() => setIsBioModalOpen(false), []);

    return (
        <>
            {!inlineHeader && <div className={styles.inlineHeader}>{detailsHeader}</div>}
            <div className={styles.container}>
                {sellerImage &&
                    ((shouldShowBioModal(seller) && !inlineBioLink) || isMobilePdpRedesign ? (
                        <Link
                            onClick={openBioModal}
                            className={
                                isMobilePdpRedesign
                                    ? styles.imageContainerRedesign
                                    : styles.imageContainer
                            }
                        >
                            {sellerImage}
                        </Link>
                    ) : (
                        <div
                            className={
                                isMobilePdpRedesign
                                    ? styles.imageContainerRedesign
                                    : styles.imageContainer
                            }
                        >
                            {sellerImage}
                        </div>
                    ))}
                <div className={styles.header}>
                    {inlineHeader && detailsHeader}
                    <div className={isMobilePdpRedesign ? styles.reviewsRedesign : styles.reviews}>
                        <div className={styles.reviewsStarRating}>
                            <SharedStarRating
                                isMobilePdpRedesign={isMobilePdpRedesign}
                                seller={seller}
                            />
                        </div>
                        <PdpSharedDealerDetailsFeedback seller={seller} isMobile={!!isMobile} />
                    </div>
                    <span className={styles.location} data-tn="dealer-location">
                        <FormattedMessage
                            id="PdpSharedDealerDetailsHeader.location"
                            defaultMessage="Located in {dealerLocation}"
                            values={{ dealerLocation }}
                        />
                    </span>
                    {isClient && (shouldShowBioModal(seller) || isMobilePdpRedesign) && (
                        <>
                            {inlineBioLink && (
                                <Link className={styles.learnMore} onClick={openBioModal}>
                                    {learnMore}
                                </Link>
                            )}
                            <PdpSharedDealerDetailsBioModalLazy
                                seller={seller}
                                isOpen={isBioModalOpen}
                                onClose={closeBioModal}
                                isMobilePdpRedesign={isMobilePdpRedesign}
                            />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};
